import { motion } from 'framer-motion';
import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  ReactNode,
  useMemo,
} from 'react';

import { useTheme } from '@/features/theme-switch';

interface IIconButtonProps
  extends Omit<
    DetailedHTMLProps<
      ButtonHTMLAttributes<HTMLButtonElement>,
      HTMLButtonElement
    >,
    'title'
  > {
  icon?: ReactNode;
  iconAfter?: ReactNode;
  title?: ReactNode;
  open?: boolean;
}

export default function IconButton({
  icon,
  open = true,
  title,
  iconAfter,
  className = '',
  ...props
}: IIconButtonProps) {
  const { darkTheme } = useTheme();

  const maxWidth = useMemo(() => (open ? 'auto' : 0), [open]);

  const pl = useMemo(() => (open ? 8 : 0), [open]);

  const color = useMemo(
    () => (darkTheme ? '#FFFFFFB8' : '#18192B'),
    [darkTheme]
  );

  return (
    <button
      type="button"
      {...props}
      className={`group relative overflow-hidden rounded p-2 ${className}`}
    >
      <div className="pointer-events-none relative z-10 flex rounded">
        <div className="flex items-center">
          {icon && (
            <div className="flex items-center justify-center">{icon}</div>
          )}
          {title && (
            <motion.div
              initial={{ maxWidth: 'auto', paddingLeft: 8 }}
              animate={{ maxWidth, paddingLeft: pl }}
              style={{ color }}
              transition={{
                maxWidth: { duration: 0.3, ease: 'easeOut' },
                opacity: { duration: 0.3, ease: 'easeOut' },
              }}
              className="overflow-hidden text-h6 font-light"
            >
              {title}
            </motion.div>
          )}
          {iconAfter && (
            <div className="flex items-center justify-center">{iconAfter}</div>
          )}
        </div>
      </div>

      <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-60 w-60 -translate-x-1/2 -translate-y-1/2 scale-0 bg-[#234DFF0A] transition-all duration-700 ease-out group-hover:scale-100 dark:bg-[#FFFFFF0A]" />
    </button>
  );
}
